import { SUPPRESSED_ERROR_CODES } from '@constants/errorCodes';

/**
 * Type for API error responses
 * Covers multiple error formats:
 * 1. Axios errors: error.response.data.error.{code, message, errorMessage}
 * 2. Nested errors: error.error.{code, message, errorMessage}
 * 3. Direct errors: error.{code, message}
 */
export interface ApiErrorResponse {
  code?: string;
  message?: string;
  error?: {
    code?: string;
    message?: string;
    errorMessage?: string;
  };
  response?: {
    data?: {
      error?: {
        code?: string;
        message?: string;
        errorMessage?: string;
      };
    };
  };
}

/**
 * Checks if an error should be suppressed from error reporting
 * @param error - The error object to check (can be Error, ApiErrorResponse, or any object)
 * @returns true if the error should be suppressed, false otherwise
 */
export function shouldSuppressError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const errorData = error as ApiErrorResponse;

  // Check all possible locations for error codes
  const errorCode =
    errorData?.response?.data?.error?.code ||
    errorData?.error?.code ||
    errorData?.code;

  // Check all possible locations for error messages (consistent with error code extraction)
  const errorMessage =
    errorData?.response?.data?.error?.message ||
    errorData?.response?.data?.error?.errorMessage ||
    errorData?.error?.message ||
    errorData?.error?.errorMessage ||
    (error instanceof Error ? error.message : undefined) ||
    errorData?.message ||
    '';

  // Check if the error code is in the suppressed list
  if (
    errorCode &&
    typeof errorCode === 'string' &&
    SUPPRESSED_ERROR_CODES.includes(errorCode)
  ) {
    return true;
  }

  // Check if the error message contains any suppressed error code
  if (errorMessage) {
    return SUPPRESSED_ERROR_CODES.some((code) => errorMessage.includes(code));
  }

  return false;
}
